import React, { useState } from 'react';
import { AlertDispatchRow, listAlertDispatch } from '../../../../api/stays-alerts-client';
import { AppButton } from '../../../button/app-button';
import { ClockNote } from '../../../clock-note/clock-note';
import { StatusPill, StatusTone } from '../../../status-pill/status-pill';
import { dateTimeIn, formatDayKey } from '../../../../function-utils/building-time';
import toast from '../../../../utils/toast';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import { deviceClock } from '../guide/guide-model';
import { StaysApiError } from '../../../../api/stays-client';
import { Card, CardTitle, Note, SentAt, Table } from './alerts.styled';

/**
 * RECENT ALERTS (plan S8, SC-1 "Recent alerts"): the listing's dispatch rows,
 * newest first. The local date and the slot are the BUILDING's (the alert's
 * own day); "Sent" is an instant, shown on the viewer's device clock.
 */

const KIND_WORDS: Record<string, string> = {
  daily_summary: 'Daily summary',
  cleaning_scheduled: 'Cleaning booked',
  cleaning_unscheduled: 'Cleaning not booked',
  turnaround: 'Same-day turnaround',
  guest_arrived: 'Guest check-in',
  reservation_created: 'New stay',
  reservation_cancelled: 'Stay cancelled',
};

const TRIGGER_WORDS: Record<string, string> = {
  cron: 'Scheduled',
  time_reached: 'Time reached',
  guest_event: 'Guest',
  desk_signin: 'Desk sign-in',
  guest_checkout: 'Guest checkout',
  manual: 'Sent now',
  batch: 'Several at once',
};

const STATUS: Record<AlertDispatchRow['status'], { tone: StatusTone; label: string }> = {
  sent: { tone: 'success', label: 'Sent' },
  skipped: { tone: 'neutral', label: 'Skipped' },
  pending: { tone: 'brand', label: 'Sending' },
  failed: { tone: 'alert', label: 'Failed' },
};

const SKIP_WORDS: Record<string, string> = {
  quiet_day: 'nothing to report',
  no_recipients: 'nobody to send to',
  toggle_off: 'switched off',
  not_configured: 'not set up',
};

const slotWords = (slot: string): string => (slot === '-' ? '—' : slot.startsWith('manual:') ? 'Now' : slot);

export const AlertHistory: React.FC<{ listingId: string; initial: AlertDispatchRow[] }> = ({ listingId, initial }) => {
  const [rows, setRows] = useState<AlertDispatchRow[]>(initial);
  const [next, setNext] = useState<string | null>(initial.length >= 20 ? initial[initial.length - 1].created_at : null);
  const [busy, setBusy] = useState(false);
  const clock = deviceClock();

  const more = async () => {
    if (!next || busy) return;
    setBusy(true);
    try {
      const page = await listAlertDispatch(listingId, next);
      setRows(r => [...r, ...page.items]);
      setNext(page.next_before);
    } catch (e) {
      toast.error(refusalText(e as StaysApiError, "Couldn't load more alerts."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card aria-labelledby="alert-history-title" data-testid="alert-history">
      <CardTitle id="alert-history-title">Recent alerts</CardTitle>
      {rows.length === 0 ? (
        <Note role="status">No alerts sent yet</Note>
      ) : (
        <>
          <ClockNote clock={clock} />
          <Table>
            <thead>
              <tr>
                <th scope="col">Kind</th>
                <th scope="col">Date</th>
                <th scope="col">Time slot</th>
                <th scope="col">Trigger</th>
                <th scope="col">Status</th>
                <th scope="col">Attempts</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={`${r.kind}-${r.local_date}-${r.slot}-${r.created_at}`}>
                  <td data-label="Kind">{KIND_WORDS[r.kind] || r.kind}</td>
                  <td data-label="Date">{formatDayKey(r.local_date)}</td>
                  <td data-label="Time slot">{slotWords(r.slot)}</td>
                  <td data-label="Trigger">{TRIGGER_WORDS[r.trigger] || r.trigger}</td>
                  <td data-label="Status">
                    <StatusPill tone={STATUS[r.status].tone}>
                      {STATUS[r.status].label}
                      {r.status === 'skipped' && r.skip_reason ? ` · ${SKIP_WORDS[r.skip_reason] || r.skip_reason}` : ''}
                    </StatusPill>
                    {r.sent_at ? (
                      <SentAt data-clock-whose="you" data-at={r.sent_at}>
                        {dateTimeIn(r.sent_at, clock.zone)}
                      </SentAt>
                    ) : null}
                  </td>
                  <td data-label="Attempts">{r.attempts}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          {next ? (
            <AppButton type="button" variant="ghost" onClick={() => void more()} loading={busy} loadingLabel="Loading">
              Show older alerts
            </AppButton>
          ) : null}
        </>
      )}
    </Card>
  );
};
